import BaseController from "./BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";
import MessageBox from "sap/m/MessageBox";
import MessageToast from "sap/m/MessageToast";
import Fragment from "sap/ui/core/Fragment";
import type Dialog from "sap/m/Dialog";
import type TextArea from "sap/m/TextArea";
import type { Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import type Context from "sap/ui/model/odata/v4/Context";
import type { ListItemBase$PressEvent } from "sap/m/ListItemBase";
import type { Switch$ChangeEvent } from "sap/m/Switch";
import type { ResponseError } from "../types/Errors";
import { problemMessage } from "../util/ProblemDetails";

interface ProductVariant {
	Id: string;
	Code: string;
	Name: string;
	LicensingMode: string;
	BillingMode: string;
	Status: string;
}

interface ViewState {
	familyId: string;
	variants: ProductVariant[];
	kpi: {
		variants: string;
		activeVariants: string;
	};
}

/**
 * @namespace computec.b2b.portal.controller
 */
export default class ProductFamilyDetail extends BaseController {
	private deactivateDialog: Dialog | null = null;

	public override onInit(): void {
		const initial: ViewState = {
			familyId: "",
			variants: [],
			kpi: { variants: "—", activeVariants: "—" }
		};
		this.getView().setModel(new JSONModel(initial), "view");
		this.getRouter().getRoute("productFamilyDetail")?.attachPatternMatched((e) => {
			this.onPatternMatched(e);
		});
	}

	private onPatternMatched(event: Route$PatternMatchedEvent): void {
		const args = event.getParameter("arguments") as { id?: string } | undefined;
		const id = args?.id ?? "";
		const view = this.getView().getModel("view") as JSONModel;
		view.setProperty("/familyId", id);

		this.getView().bindElement({
			path: `/ProductFamilies(${id})`,
			model: "odata"
		});

		void this.loadVariants(id);
	}

	private async loadVariants(familyId: string): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const data = await fetcher.json<{ value: ProductVariant[] }>(
				`/odata/v4/Products?$filter=ProductFamilyId%20eq%20${familyId}&$orderby=Code&$select=Id,Code,Name,LicensingMode,BillingMode,Status`
			);
			const variants = Array.isArray(data.value) ? data.value : [];
			view.setProperty("/variants", variants);
			view.setProperty("/kpi/variants", String(variants.length));
			view.setProperty("/kpi/activeVariants", String(variants.filter(v => v.Status === "Active").length));
		} catch {
			view.setProperty("/variants", []);
		}
	}

	public onNavBack(): void {
		this.navTo("productFamilies", {});
	}

	public onRefresh(): void {
		const id = (this.getView().getModel("view") as JSONModel).getProperty("/familyId") as string;
		if (id) {
			this.getView().getModel("odata")?.refresh();
			void this.loadVariants(id);
		}
	}

	public onOpenProduct(event: ListItemBase$PressEvent): void {
		const ctx = event.getSource().getBindingContext("view") as Context | null;
		if (!ctx) {
			return;
		}
		const productId = ctx.getProperty("Id") as string | undefined;
		if (productId) {
			this.navTo("productDetail", { id: productId });
		}
	}

	/**
	 * Toggle ProductFamily.IsHidden. Endpoint
	 * <c>PATCH /api/v1/product-families/{id}/visibility</c> with body <c>{ hidden: bool }</c>
	 * (permission <c>catalog.manage</c>). The switch is bound to <c>odata>IsHidden</c> —
	 * on error we revert the state manually, on success we force a refresh.
	 */
	public async onVisibilityChanged(event: Switch$ChangeEvent): Promise<void> {
		const sw = event.getSource();
		const hidden = sw.getState();
		const id = (this.getView().getModel("view") as JSONModel).getProperty("/familyId") as string;
		if (!id) {
			return;
		}

		const bundle = await this.getResourceBundle();
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const r = await fetcher.fetch(`/api/v1/product-families/${id}/visibility`, {
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ hidden })
			});
			if (!r.ok) {
				const bodyText = await r.text();
				const err = new Error(`HTTP ${r.status}`) as Error & ResponseError;
				err.status = r.status;
				err.bodyText = bodyText;
				throw err;
			}
			MessageToast.show(bundle.getText("product.visibility.savedToast") ?? "Visibility updated");
			this.getView().getModel("odata")?.refresh();
		} catch (e) {
			sw.setState(!hidden);
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("product.visibility.errorToast") ?? "Failed to update visibility"));
		}
	}

	// === Lifecycle (POST /api/v1/product-families/{id}/activate | /deactivate) ===

	/**
	 * A family created from the portal starts Inactive, exactly like a Product, and this is
	 * the only action that moves it out of that state. REST endpoints, so the OData v4 cache
	 * has to be refreshed explicitly afterwards.
	 */
	public async onActivateFamily(): Promise<void> {
		const id = (this.getView().getModel("view") as JSONModel).getProperty("/familyId") as string;
		if (!id) {
			return;
		}
		const bundle = await this.getResourceBundle();
		const name = (this.getView().getBindingContext("odata")?.getProperty("Name") as string) ?? "";
		MessageBox.confirm(bundle.getText("productFamilyDetail.activate.confirm", [name]) ?? "Activate this family?", {
			title: bundle.getText("productFamilyDetail.action.activate") ?? "Activate",
			onClose: (action: unknown) => {
				if (action === MessageBox.Action.OK) {
					void this.sendLifecycle(
						`/api/v1/product-families/${id}/activate`,
						"{}",
						bundle.getText("productFamilyDetail.activate.success") ?? "Product family activated.",
						bundle.getText("productFamilyDetail.activate.failure") ?? "Could not activate the product family."
					);
				}
			}
		});
	}

	public async onDeactivateFamily(): Promise<void> {
		const bundle = await this.getResourceBundle();
		(this.getView().getModel("view") as JSONModel).setProperty(
			"/deactivateTitle",
			bundle.getText("productFamilyDetail.action.deactivate") ?? "Deactivate"
		);
		await this.openDeactivateDialog();
	}

	public async onConfirmDeactivate(): Promise<void> {
		const id = (this.getView().getModel("view") as JSONModel).getProperty("/familyId") as string;
		if (!id) {
			return;
		}
		const bundle = await this.getResourceBundle();
		const ta = this.byId("taDeactivateReason") as TextArea | undefined;
		const reason = (ta?.getValue() ?? "").trim();
		// Backend validator: Reason NotEmpty, MaximumLength(500).
		if (reason.length === 0) {
			ta?.setValueState("Error");
			ta?.setValueStateText(bundle.getText("catalog.deactivate.reasonRequired") ?? "Reason is required.");
			return;
		}
		ta?.setValueState("None");
		this.deactivateDialog?.close();
		await this.sendLifecycle(
			`/api/v1/product-families/${id}/deactivate`,
			JSON.stringify({ reason }),
			bundle.getText("productFamilyDetail.deactivate.success") ?? "Product family deactivated.",
			bundle.getText("productFamilyDetail.deactivate.failure") ?? "Could not deactivate the product family."
		);
	}

	public onCancelDeactivate(): void {
		this.deactivateDialog?.close();
	}

	private async openDeactivateDialog(): Promise<void> {
		this.deactivateDialog ??= await Fragment.load({
			id: this.getView().getId(),
			name: "computec.b2b.portal.view.DeactivateCatalogDialog",
			controller: this
		}) as Dialog;
		this.getView().addDependent(this.deactivateDialog);
		const ta = this.byId("taDeactivateReason") as TextArea | undefined;
		ta?.setValue("");
		ta?.setValueState("None");
		this.deactivateDialog.open();
	}

	private async sendLifecycle(url: string, body: string, successText: string, failureText: string): Promise<void> {
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(url, { method: "POST", body });
			MessageToast.show(successText);
			this.onRefresh();
		} catch (e) {
			MessageBox.error(problemMessage((e as ResponseError).bodyText, failureText));
		}
	}
}

